import { Injectable, effect, inject, signal } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { themeSwitcher } from '@siemens/ix';

export type ThemeType = "light" | "dark" | "auto"

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  translateService = inject(TranslateService);

  // Theme settings
  #currentTheme = signal<ThemeType>(localStorage.getItem("theme") as ThemeType ?? "auto")
  readonly currentTheme = this.#currentTheme.asReadonly();
  
  // Language settings
  readonly availableLanguages = ["en", "de"] // TODO: Make this more generic (e.g. by reading available languages from the translation files)
  readonly #browserLanguage = this.translateService.getBrowserLang() ?? 'en';
  #currentLanguage = signal<string>(localStorage.getItem("language")
    ?? (this.availableLanguages.includes(this.#browserLanguage)
      ? this.#browserLanguage
      : "en"))
  readonly currentLanguage = this.#currentLanguage.asReadonly();

  /**
   * Set the language of the application
   * @param lang The language to set (e.g. "en", "de"). Falls back to English if language is not available.
   */
  setLanguage(lang: string) {
    // Fallback to English if language being set is not available
    if (!this.availableLanguages.includes(lang)) {
      console.warn(`Language ${lang} is not available. Fallback to English`)
      lang = "en"
    }

    this.#currentLanguage.set(lang)
  }
 

  constructor() {
    // Initialize language settings, fallback to either browser language or English
    this.translateService.setDefaultLang("en")
    this.translateService.use(this.currentLanguage())
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.#currentLanguage.set(event.lang)
    })
    effect(() => {
      this.translateService.use(this.currentLanguage())
      localStorage.setItem("language", this.currentLanguage())
    })

    // React on changes to the theme
    themeSwitcher.themeChanged.on((theme: string) => {
      this.#currentTheme.set(this.toThemeType(theme))
    })
    effect(() => {
      this.setTheme(this.currentTheme())
      localStorage.setItem("theme", this.currentTheme())
    })
  }


  /**
   * Helper function to properly set the theme in iX
   * @param theme The theme to set (e.g. "light", "dark" or "auto")
   */
  private setTheme(theme: ThemeType) {
    if (theme === "auto") {
      themeSwitcher.setVariant()
    } else {
      const currentTheme = this.toThemeType(themeSwitcher.getCurrentTheme())
      // Only set the theme if it is different from the current theme --> otherwise: infinite loop
      if(currentTheme !== theme) {
        themeSwitcher.setVariant(theme)
      } 
    }
  }

  /**
   * Helper functions to convert a theme name (e.g theme-brand-dark) to a ThemeType (e.g. dark)
   * @param themeName String representation of a theme (e.g. theme-brand-dark)
   * @returns The ThemeType (e.g. dark)
   */
  private toThemeType(themeName: string): ThemeType {
    const r = /-((light)|(dark))$/
    const match = themeName.match(r)
    const result = match?.[1]
    return result ? result as ThemeType: "auto"
  }
}
